/**
 * Boli Live - Profile Page
 *
 * User profile, auth, and settings.
 */

import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../lib/stores/auth-store';
import { API_BASE_URL } from '../lib/api-config';
import {
  User,
  Settings,
  CreditCard,
  Package,
  LogOut,
  Shield,
  ChevronRight,
  Wallet,
  Star,
  BadgeCheck,
  Mail,
  Lock,
} from 'lucide-react';
import { getInitials, generateAvatarColor, cn } from '../lib/utils';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, signOut, kycStatus, signInWithEmail } = useAuthStore();
  const [email, setEmail] = useState('test@boli.com');
  const [password, setPassword] = useState('Test123456');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [walletBalance, setWalletBalance] = useState<number>(0);

  // Fetch wallet balance
  useEffect(() => {
    if (isAuthenticated) {
      const fetchBalance = async () => {
        try {
          const token = localStorage.getItem('boli_auth_token');
          const response = await fetch(`${API_BASE_URL}/api/wallet/balance`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            const data = await response.json();
            setWalletBalance(data.wallet?.balance || 0);
          }
        } catch {
          // Ignore errors
        }
      };
      fetchBalance();
    }
  }, [isAuthenticated]);

  const handleGoogleSignIn = async () => {
    alert('Google Sign-In would be implemented here');
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const success = await signInWithEmail(email, password);
      if (!success) {
        setError('Invalid email or password');
      }
    } catch {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-background">
        <motion.div
          className="relative text-center mb-10"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold mb-3 text-primary">
            Boli<span className="text-foreground">Live</span>
          </h1>
          <p className="text-muted-foreground text-lg">
            Pakistan's Premier Live Auction Platform
          </p>
        </motion.div>

        <motion.div
          className="relative bg-card border border-border rounded-3xl p-8 max-w-sm w-full shadow-lg"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <h2 className="text-xl font-bold mb-2 text-center text-foreground">Welcome Back</h2>
          <p className="text-muted-foreground text-sm text-center mb-6">
            Sign in to bid on live auctions
          </p>

          {/* Email/Password Login for Testing */}
          <form onSubmit={handleEmailSignIn} className="space-y-3 mb-4">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            {error && <p className="text-red-500 text-sm text-center">{error}</p>}
            <motion.button
              type="submit"
              disabled={isLoading}
              className="w-full px-6 py-4 bg-primary text-white rounded-2xl font-semibold hover:bg-primary/90 transition-all duration-300 shadow-sm disabled:opacity-50"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isLoading ? 'Signing in...' : 'Sign In with Email'}
            </motion.button>
          </form>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-card text-muted-foreground">or</span>
            </div>
          </div>

          <motion.button
            onClick={handleGoogleSignIn}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white text-black border border-gray-200 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-sm"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </motion.button>

          <p className="text-xs text-muted-foreground mt-6 text-center">
            By signing in, you agree to our{' '}
            <span className="text-primary hover:underline cursor-pointer">Terms of Service</span>
          </p>
        </motion.div>

        {/* Features */}
        <motion.div
          className="relative grid grid-cols-3 gap-4 mt-10 max-w-sm w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {[
            { icon: Shield, label: 'Secure' },
            { icon: Wallet, label: 'Easy Pay' },
            { icon: Star, label: 'Rewards' },
          ].map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-card border border-border shadow-sm"
            >
              <Icon className="w-5 h-5 text-primary" />
              <span className="text-xs text-muted-foreground">{label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20 bg-background">
      {/* Header with simple gradient or solid color */}
      <div className="relative h-32 bg-primary">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/10" />
      </div>

      {/* Profile Card - Overlapping header */}
      <div className="relative px-4 -mt-16">
        <motion.div
          className="bg-card border border-border rounded-3xl p-6 shadow-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-4">
            <motion.div
              className="relative w-20 h-20 rounded-2xl flex items-center justify-center text-2xl font-bold shadow-lg text-white"
              style={{ backgroundColor: generateAvatarColor(user?.email || '') }}
              whileHover={{ scale: 1.05 }}
            >
              {getInitials(`${user?.firstName} ${user?.lastName}`)}
              {kycStatus?.tier === 'tier2' && (
                <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary flex items-center justify-center shadow-lg border-2 border-card">
                  <BadgeCheck className="w-3 h-3 text-white" />
                </div>
              )}
            </motion.div>
            <div className="flex-1">
              <h2 className="font-bold text-xl text-foreground">
                {user?.firstName} {user?.lastName}
              </h2>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* KYC Status */}
      {kycStatus && (
        <motion.div
          className="px-4 mt-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="bg-card rounded-2xl p-5 border border-primary/20 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">
                    {kycStatus.tier === 'tier2' ? 'Fully Verified' : 'Basic Verification'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Max bid: PKR {kycStatus.maxBidAmount.toLocaleString()}
                  </p>
                </div>
              </div>
              {kycStatus.tier !== 'tier2' && (
                <button className="px-4 py-2 rounded-xl bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors">
                  Upgrade
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Menu Items */}
      <motion.div
        className="px-4 mt-6 space-y-2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <MenuItem icon={<User />} label="Edit Profile" />
        <MenuItem
          icon={<Wallet />}
          label="Bidding Wallet"
          badge={`PKR ${walletBalance.toLocaleString()}`}
          onClick={() => navigate('/wallet')}
        />
        <MenuItem icon={<CreditCard />} label="Payment Methods" />
        <MenuItem icon={<Package />} label="My Orders" onClick={() => navigate('/orders')} />
        <MenuItem icon={<Settings />} label="Settings" />
        <div className="pt-4">
          <MenuItem
            icon={<LogOut />}
            label="Sign Out"
            onClick={signOut}
            destructive
          />
        </div>
      </motion.div>
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  destructive = false,
  badge,
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  destructive?: boolean;
  badge?: string;
}) {
  return (
    <motion.button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 bg-card border border-border shadow-sm',
        destructive
          ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
          : 'text-foreground hover:bg-secondary'
      )}
      whileHover={{ x: 4 }}
      whileTap={{ scale: 0.98 }}
    >
      <div
        className={cn(
          'w-10 h-10 rounded-xl flex items-center justify-center',
          destructive ? 'bg-red-100 text-red-500 dark:bg-red-900/20' : 'bg-secondary text-foreground'
        )}
      >
        {icon}
      </div>
      <span className="flex-1 text-left font-medium">{label}</span>
      {badge && (
        <span className="text-sm text-muted-foreground font-display">{badge}</span>
      )}
      <ChevronRight className="w-5 h-5 text-muted-foreground" />
    </motion.button>
  );
}
